import { Observable, Observer, of, throwError } from 'rxjs';
import { Strings } from '../generated/strings';
import { Base64ArrayBuffer } from './base64-array-buffer';

/**
 * Unescape non-binary string code
 * @param data A value representing original non-binary string code
 */
declare function unescape(data: string): string;

/**
 * Web Crypto interface class.
 * @dynamic
 */
export class Crypto {
    private static algRsaOaepSha1Key: any = { name: 'RSA-OAEP', hash: { name: 'SHA-1' } };
    private static algHmacSha256Key: any = { name: 'HMAC', hash: { name: 'SHA-256' } };

    /**
     * hash with SHA-256
     * (If it doesn't support web based crypto, encode as base64.)
     * @param data the original data to hash.
     * @return Observable<string> the hash generated.
     */
    public static getSha256(data: string): Observable<string> {
        if (data == null) {
            const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ArgumentNullError.message;
            return throwError(() => new Error(message.format('Crypto/getSha256', 'data')));
        }

        const root = <any>window;
        const isIE = !!root.msCrypto;
        if (isIE && root.msCrypto.subtle) {
            return Crypto.hash256IE(root.msCrypto.subtle, data);
        }

        if (root.crypto) {
            const subtle = root.crypto.subtle || root.crypto.webkitSubtle;
            if (subtle) {
                return Crypto.hash256WebAPI(subtle, data);
            }
        }

        return of(window.btoa(data));
    }

    /**
     * encrypt with RSA/SHA-1
     *
     * @param jwk the JSON Web Key. Single string with JSON.stringify format.
     * @param data the original data to hash.
     * @return Observable<string> the hash generated.
     */
    public static encryptRsaSha1(jwk: string, data: string): Observable<string> {
        const RsaSha1 = 'Crypto/encryptRsaSha1';
        let message = '';
        if (jwk == null) {
            message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ArgumentNullError.message;
            return throwError(() => new Error(message.format(RsaSha1, 'jwk')));
        }

        if (data == null) {
            message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ArgumentNullError.message;
            return throwError(() => new Error(message.format(RsaSha1, 'data')));
        }

        const root = <any>window;
        const isIE = !!root.msCrypto;
        if (isIE && root.msCrypto.subtle) {
            return Crypto.encryptRsaIE(root.msCrypto.subtle, jwk, data);
        }

        if (root.crypto) {
            let subtle = root.crypto.subtle;
            if (subtle) {
                const jwkObject = JSON.parse(jwk);
                return Crypto.encryptRsaWebAPI(subtle, jwkObject, data);
            }

            subtle = root.crypto.webkitSubtle;
            if (subtle) {
                const jwkArray = Crypto.toArrayBufferView(jwk);
                return Crypto.encryptRsaWebAPI(subtle, jwkArray, data);
            }
        }

        message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.UnknownBrowser.message;
        return throwError(() => new Error(message.format(RsaSha1)));
    }

    /**
     * sign with HMAC/SHA-256
     *
     * @param key the key (base64 encoded).
     * @param data the original data to hash. (unicode - not utf8)
     * @return Observable<string> the hash generated.
     */
    public static signHmac(key: string, data: string): Observable<string> {
        let message = '';
        if (key == null) {
            message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ArgumentNullError.message;
            return throwError(() => new Error(message.format('Crypto/signHmac', 'key')));
        }

        if (data == null) {
            message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ArgumentNullError.message;
            return throwError(() => new Error(message.format('Crypto/encryptRsaSha1', 'data')));
        }

        const root = <any>window;
        const isIE = !!root.msCrypto;
        if (isIE && root.msCrypto.subtle) {
            return Crypto.signHmacIE(root.msCrypto.subtle, key, data);
        }

        if (root.crypto) {
            const subtle = root.crypto.subtle || root.crypto.webkitSubtle;
            if (subtle) {
                return Crypto.signHmacWebAPI(subtle, key, data);
            }
        }

        message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.UnknownBrowser.message;
        return throwError(() => new Error(message.format('Crypto/signHmac')));
    }

    /**
     * Hash with SHA-256 for WebAPI.
     *
     * @param subtle the Web API subtle.
     * @param data the original data to hash.
     * @return Observable<string> the hash generated.
     */
    private static hash256WebAPI(subtle: SubtleCrypto, data: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            const array = Crypto.toUint8Array(data);
            subtle.digest({ name: 'SHA-256' }, array)
                .then(
                (hash: ArrayBuffer) => {
                    const hexText = Crypto.toHexString(hash);
                    observer.next(hexText);
                    observer.complete();
                },
                (error: any) => {
                    observer.error(error);
                });
        });
    }

    /**
     * Hash with SHA-256 for IE/Older API.
     *
     * @param subtle the older API subtle.
     * @param data the original data to hash.
     * @return Observable<string> the hash generated.
     */
    private static hash256IE(subtle: any, data: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            const array = Crypto.toUint8Array(data);
            const cryptoOp: any = subtle.digest({ name: 'SHA-256' }, array);
            cryptoOp.oncomplete = (event: any) => {
                const hexText = Crypto.toHexString(event.target.result);
                observer.next(hexText);
                observer.complete();
            };
            cryptoOp.onerror = (error: any) => {
                observer.error(error);
            };
            cryptoOp.finish();
        });
    }

    /**
     * Encrypt with RSA/SHA-1 for WebAPI.
     *
     * @param subtle the Web API subtle.
     * @param jwkObject the JSON Web key object or ArrayBufferView on webkit.
     * @param data the original data to encrypt.
     * @return Observable<string> the encrypted base64 string.
     */
    private static encryptRsaWebAPI(subtle: SubtleCrypto, jwkObject: any, data: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            const array = Crypto.toUtf8ArrayBuffer(data);
            subtle.importKey('jwk', jwkObject, Crypto.algRsaOaepSha1Key, false, ['encrypt'])
                .then((publicKey: any) => {
                    return subtle.encrypt(Crypto.algRsaOaepSha1Key, publicKey, <any>array);
                })
                .then(
                (result: ArrayBuffer) => {
                    observer.next(Crypto.createBase64(result));
                    observer.complete();
                },
                (error: any) => {
                    observer.error(error);
                });
        });
    }

    /**
     * Encrypt with RSA/SHA-1 for IE/Older API.
     *
     * @param subtle the older API subtle.
     * @param jwk the JSON Web key format (Stringfiy).
     * @param data the original data to hash.
     * @return Observable<string> the encrypted base64 string.
     */
    private static encryptRsaIE(subtle: any, jwk: string, data: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            const array = Crypto.toUtf8ArrayBuffer(data);
            const onerror = (error: any) => {
                observer.error(error);
            };
            const jwkBytes = Crypto.toArrayBuffer(jwk);
            const importKeyOp: any = subtle.importKey('jwk', jwkBytes, Crypto.algRsaOaepSha1Key, false, ['encrypt']);
            importKeyOp.oncomplete = (importKeyOpOnCompleteEvent: any) => {
                const publicKey: any = importKeyOpOnCompleteEvent.target.result;
                const encryptOp: any = subtle.encrypt(Crypto.algRsaOaepSha1Key, publicKey, array);
                encryptOp.oncomplete = (encryptOpOnCompleteEvent: any) => {
                    observer.next(Crypto.createBase64(encryptOpOnCompleteEvent.target.result));
                    observer.complete();
                };
                encryptOp.onerror = onerror;
            };
            importKeyOp.onerror = onerror;
        });
    }

    /**
     * Sign with HMAC for WebAPI.
     *
     * @param subtle the Web API subtle.
     * @param key the key.
     * @param data the original data to encrypt.
     * @return Observable<string> the encrypted base64 string.
     */
    private static signHmacWebAPI(subtle: SubtleCrypto, key: string, data: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            const array = Crypto.toUtf8ArrayBuffer(data);
            const keyObject = <any>Crypto.toArrayBufferView(window.atob(key));
            subtle.importKey('raw', keyObject, Crypto.algHmacSha256Key, false, ['sign'])
                .then((publicKey: any) => {
                    return subtle.sign(Crypto.algHmacSha256Key, publicKey, <any>array);
                })
                .then(
                (result: ArrayBuffer) => {
                    observer.next(Crypto.createBase64(result));
                    observer.complete();
                },
                (error: any) => {
                    observer.error(error);
                });
        });
    }

    /**
     * Sign with HMAC for IE/Older API.
     *
     * @param subtle the older API subtle.
     * @param jwk the key.
     * @param data the original data to hash.
     * @return Observable<string> the encrypted base64 string.
     */
    private static signHmacIE(subtle: any, key: string, data: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            const array = Crypto.toUtf8ArrayBuffer(data);
            const onerror = (error: any) => {
                observer.error(error);
            };
            const keyBytes = Crypto.toArrayBuffer(window.atob(key));
            const importKeyOp: any = subtle.importKey('raw', keyBytes, Crypto.algHmacSha256Key, false, ['sign']);
            importKeyOp.oncomplete = (importKeyOpEvent: any) => {
                const publicKey: any = importKeyOpEvent.target.result;
                const encryptOp: any = subtle.sign(Crypto.algHmacSha256Key, publicKey, array);
                encryptOp.oncomplete = (encryptOpEvent: any) => {
                    observer.next(Crypto.createBase64(encryptOpEvent.target.result));
                    observer.complete();
                };
                encryptOp.onerror = onerror;
            };
            importKeyOp.onerror = onerror;
        });
    }

    /**
     * Create hex string from byte raw data.
     *
     * @param bytes the array buffer.
     * @return string the hex string.
     */
    private static toHexString(bytes: ArrayBuffer): string {
        const array = new Uint8Array(bytes);
        let hexText = '';
        for (let i = 0; i < array.length; i++) {
            const hex = array[i].toString(16);
            hexText += (hex.length === 1 ? '0' : '') + hex;
        }

        return hexText;
    }

    /**
     * Create Uint8Array from a string.
     *
     * @param data The string data.
     * @return Uint8Array The bytes array.
     */
    private static toUint8Array(data: string): Uint8Array {
        const temp: number[] = [];
        for (let i = 0; i < data.length; i++) {
            const ch = data.charCodeAt(i);
            temp.push((ch & 0x0ff00) >> 8);
            temp.push(ch & 0x0ff);
        }

        return new Uint8Array(temp);
    }

    /**
     * Create ArrayBuffer from a string with unicode.
     *
     * @param data The string data.
     * @return ArrayBuffer The bytes array.
     */
    private static toUtf8ArrayBuffer(data: string): ArrayBuffer {
        const utf8 = Crypto.utf8Encode(data);
        return Crypto.toArrayBuffer(utf8);
    }

    /**
     * Encode utf8 string.
     *
     * @param data the unencoded string.
     */
    private static utf8Encode(data: string): string {
        return unescape(encodeURIComponent(data));
    }

    /**
     * Create ArrayBuffer from a string with unicode.
     *
     * @param data The string data.
     * @return ArrayBuffer The bytes array.
     */
    private static toArrayBuffer(data: string): ArrayBuffer {
        const buffer = new ArrayBuffer(data.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < buffer.byteLength; i++) {
            view[i] = data.charCodeAt(i);
        }

        return buffer;
    }

    /**
     * Create ArrayBufferView from a string with unicode.
     *
     * @param data The string data.
     * @return ArrayBuffer The bytes array.
     */
    private static toArrayBufferView(data: string): ArrayBufferView {
        const buffer = new ArrayBuffer(data.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < buffer.byteLength; i++) {
            view[i] = data.charCodeAt(i);
        }

        return view;
    }

    /**
     * Create base64 from byte data.
     *
     * @param data The byte data.
     * @return string The base64 encoded data.
     */
    private static createBase64(data: ArrayBuffer): string {
        return Base64ArrayBuffer.Encode(data);
    }
}
